import { SolidifyableData } from "@lib/titan2/dist/api/core/data/SolidifyableData";
import { ApiConstants } from "../enum/Constants";
/**
 * SlotsSpinData.ts
 */
export class SlotsSpinData extends SolidifyableData {
    constructor(data) {
        super(data);
        this.solidify();
    }
    solidify() {
        super.solidify();
        if (this.dataExists(this.data)) {
            this.spinType = this.data.getString(ApiConstants.PROPERTY_SPIN_TYPE);
            this.reelSet = this.data.getString(ApiConstants.PROPERTY_REEL_SET);
            this.reels = this.data.getString(ApiConstants.PROPERTY_REELS);
            this.payout = new SlotsPayoutData(this.data.getDataObject(ApiConstants.PROPERTY_PAYOUT));
            this.freeSpinsUsed = this.data.getNumber(ApiConstants.PROPERTY_FREE_SPINS_USED);
            this.freeSpinsRemaining = this.data.getNumber(ApiConstants.PROPERTY_FREE_SPINS_REMAINING);
            this.freeSpinsTriggered = this.data.getNumber(ApiConstants.PROPERTY_FREE_SPINS_TRIGGERED);
            this.totalWin = this.data.getNumber(ApiConstants.PROPERTY_TOTAL_WIN);
            this.feature = this.data.getString(ApiConstants.PROPERTY_FEATURE);
            this.solidifySymbolArray();
            this.solidifyPayoutsArray();
            this.columns = this.data.getNumber(ApiConstants.PROPERTY_NUMBER_OF_COLUMNS);
            this.expandColumns = this.data.getNumber(ApiConstants.PROPERTY_EXPAND_COLUMNS);
        }
    }
    solidifySymbolArray() {
        if (this.dataExists(this.data)) {
            const spinArrayObj = this.data.getDataArray(ApiConstants.PROPERTY_SYMBOLS).map(dd => dd.toObject());
            this.symbols = spinArrayObj.map(spinObj => {
                let ret = [];
                if (Array.isArray(spinObj)) {
                    ret = spinObj.map(obj => new SlotsSymbolType(obj));
                }
                return ret;
            });
        }
    }
    solidifyPayoutsArray() {
        const data = this.data.getDataArray(ApiConstants.PROPERTY_PAYOUTS);
        if (this.dataExists(data)) {
            this.payouts = data.map(dd => new SlotsPayoutData(dd));
        }
    }
    getSpinType() {
        return this.spinType;
    }
    getReelSet() {
        return this.reelSet;
    }
    getReels() {
        return this.reels;
    }
    getPayout() {
        return this.payout;
    }
    getFreeSpinsUsed() {
        return this.freeSpinsUsed;
    }
    getFreeSpinsRemaining() {
        return this.freeSpinsRemaining;
    }
    getFreeSpinsTriggered() {
        return this.freeSpinsTriggered;
    }
    getTotalWin() {
        return this.totalWin;
    }
    getFeature() {
        return this.feature;
    }
    getSymbols() {
        if (this.symbols && this.symbols.length) {
            return this.symbols.map(s => s.slice());
        }
        return null;
    }
    getPayouts() {
        return this.payouts?.slice() || null;
    }
    getColumns() {
        return this.columns;
    }
    getExpandColumns() {
        return this.expandColumns;
    }
}
/**
 * SlotsPayoutData.ts
 */
export class SlotsPayoutData extends SolidifyableData {
    constructor(data) {
        super(data);
        this.solidify();
    }
    solidify() {
        super.solidify();
        if (this.dataExists(this.data)) {
            this.column = this.data.getNumber(ApiConstants.PROPERTY_COLUMN);
            this.row = this.data.getNumber(ApiConstants.PROPERTY_ROW);
            this.locked = this.data.getBool(ApiConstants.PROPERTY_LOCKED);
            this.symbol = new SlotsSymbolType(this.data.getString(ApiConstants.PROPERTY_SYMBOL));
            this.winMultiplier = this.data.getNumber(ApiConstants.PROPERTY_WIN_MULTIPLIER);
            this.multiplier = this.data.getNumber(ApiConstants.PROPERTY_MULTIPLIER);
            this.win = this.data.getNumber(ApiConstants.PROPERTY_WIN);
            this.params = new SlotsPayoutParamsData(this.data.getDataObject(ApiConstants.PROPERTY_PARAMS));
        }
    }
    getColumn() {
        return this.column;
    }
    getRow() {
        return this.row;
    }
    getLocked() {
        return this.locked;
    }
    getSymbol() {
        return this.symbol;
    }
    getWinMultiplier() {
        return this.winMultiplier;
    }
    getMultiplier() {
        return this.multiplier;
    }
    getWin() {
        return this.win;
    }
    getParams() {
        return this.params;
    }
}
/**
 * SlotsPayoutParamsData.ts
 */
export class SlotsPayoutParamsData extends SolidifyableData {
    constructor(data) {
        super(data);
        this.solidify();
    }
    solidify() {
        super.solidify();
        if (this.dataExists(this.data)) {
            this.effectFrequencies = this.data.getNumber(ApiConstants.PROPERTY_PARAMS_EFFECT_FREQUENCIES);
            this.effectMultiplier = this.data.getNumber(ApiConstants.PROPERTY_PARAMS_EFFECT_MULTIPLIER);
            this.solidifyEffectTargets();
            this.originalAmount = this.data.getNumber(ApiConstants.PROPERTY_PARAMS_ORIGINAL_AMOUNT);
            this.originalMultiplier = this.data.getNumber(ApiConstants.PROPERTY_PARAMS_ORIGINAL_MULTIPLIER);
        }
    }
    solidifyEffectTargets() {
        //example input: "[2,4;1,1;0,2;],[0,3;1,1;0,3;],[3,4;0,3;2,0;2,0;3,4;2,0;0,2;]"
        const dataString = this.data.getString(ApiConstants.PROPERTY_PARAMS_EFFECT_TARGETS);
        if (dataString) {
            const trimmed = dataString.substring(1, dataString.length - 1); // "2,4;1,1;0,2;],[0,3;1,1;0,3;],[3,4;0,3;2,0;2,0;3,4;2,0;0,2;" 
            const split0 = trimmed.split("],["); // ["2,4;1,1;0,2;","0,3;1,1;0,3;","3,4;0,3;2,0;2,0;3,4;2,0;0,2;"]
            const last = split0[split0.length - 1]; // "3,4;0,3;2,0;2,0;3,4;2,0;0,2;"
            const split1 = last.split(";"); // ["3,4","0,3","2,0","2,0","3,4","2,0","0,2", ""]
            const filtered = split1.filter(spl => spl.length); // ["3,4","0,3","2,0","2,0","3,4","2,0","0,2"]
            const split2 = filtered.map(spl => spl.split(",")); // [["3","4"],["0","3"],["2","0"],["2","0"],["3","4"],["2","0"],["0","2"]]        
            this.effectTargets = split2.map(([row, col]) => { return { row: parseInt(row, 10), column: parseInt(col, 10) }; }); //They're in order (y,x) for some reason
        }
    }
    getEffectFrequencies() {
        return this.effectFrequencies;
    }
    getEffectMultiplier() {
        return this.effectMultiplier;
    }
    getOriginalAmount() {
        return this.originalAmount;
    }
    getEffectTargets() {
        return this.effectTargets?.slice() || [];
    }
    getOriginalMultiplier() {
        return this.originalMultiplier;
    }
}
/**
 * SlotsSymbolType.ts
 */
export class SlotsSymbolType {
    constructor(type) {
        this.type = type;
    }
    static getType(type) {
        return SlotsSymbolType[type];
    }
    isBlank() {
        return this.type === SlotsSymbolType.BLANK.getType();
    }
    isCoin() {
        return this.type === SlotsSymbolType.COIN.getType();
    }
    isGoldCoin() {
        return this.type === SlotsSymbolType.GOLD_COIN.getType();
    }
    isCollector() {
        return this.type === SlotsSymbolType.COLLECTOR.getType();
    }
    isPayer() {
        return this.type === SlotsSymbolType.PAYER.getType();
    }
    isCollectorPayer() {
        return this.type === SlotsSymbolType.COLLECTORPAYER.getType();
    }
    isSniper() {
        return this.type === SlotsSymbolType.SNIPER.getType();
    }
    isResetplus1() {
        return this.type === SlotsSymbolType.RESETPLUS1.getType();
    }
    isNecromancer() {
        return this.type === SlotsSymbolType.NECROMANCER.getType();
    }
    isPersistentPayer() {
        return this.type === SlotsSymbolType.PERSISTENTPAYER.getType();
    }
    isPersistentCollector() {
        return this.type === SlotsSymbolType.PERSISTENTCOLLECTOR.getType();
    }
    isPersistentSniper() {
        return this.type === SlotsSymbolType.PERSISTENTSNIPER.getType();
    }
    isPersistent() {
        return this.isPersistentCollector() || this.isPersistentPayer() || this.isPersistentSniper();
    }
    getType() {
        return this.type;
    }
    toString() {
        return this.type;
    }
}
SlotsSymbolType.BLANK = new SlotsSymbolType("BLANK");
SlotsSymbolType.COIN = new SlotsSymbolType("COIN");
SlotsSymbolType.GOLD_COIN = new SlotsSymbolType("GOLD_COIN");
SlotsSymbolType.COLLECTOR = new SlotsSymbolType("COLLECTOR");
SlotsSymbolType.PAYER = new SlotsSymbolType("PAYER");
SlotsSymbolType.COLLECTORPAYER = new SlotsSymbolType("COLLECTOR_PAYER");
SlotsSymbolType.SNIPER = new SlotsSymbolType("SNIPER");
SlotsSymbolType.RESETPLUS1 = new SlotsSymbolType("RESET_PLUS");
SlotsSymbolType.NECROMANCER = new SlotsSymbolType("NECROMANCER");
SlotsSymbolType.PERSISTENTPAYER = new SlotsSymbolType("PERSISTENT_PAYER");
SlotsSymbolType.PERSISTENTCOLLECTOR = new SlotsSymbolType("PERSISTENT_COLLECTOR");
SlotsSymbolType.PERSISTENTSNIPER = new SlotsSymbolType("PERSISTENT_SNIPER");
//# sourceMappingURL=SlotsSpinsData.js.map